import { createPollingInterval } from "@sg-protect/core";
import { useMemo, useRef, useState } from "react";

interface UsePollingIntervalOptions<TData> {
    isPending: (data: TData | undefined) => boolean;
    interval?: number;
    timeout: number;
    isEnabled?: boolean;
}

export function usePollingInterval<TData>({
    isPending,
    interval,
    timeout,
    isEnabled = true
}: UsePollingIntervalOptions<TData>) {
    const [isPolling, setIsPolling] = useState(isEnabled);

    const isPendingRef = useRef(isPending);
    isPendingRef.current = isPending;

    // isPendingRef keeps the predicate in sync without resetting the clock when it changes identity.
    const pollingInterval = useMemo(
        () =>
            createPollingInterval<TData>({
                isPending: data => isPendingRef.current(data),
                interval,
                timeout
            }),
        [interval, timeout, isPendingRef]
    );

    const wasEnabledRef = useRef(isEnabled);

    if (wasEnabledRef.current && !isEnabled) {
        pollingInterval.reset();
        setIsPolling(false);
    }

    wasEnabledRef.current = isEnabled;

    const refetchInterval = (query: { state: { data?: TData } }) => {
        if (!isEnabled) {
            return false as const;
        }

        const result = pollingInterval(query);

        setIsPolling(result !== false);

        return result;
    };

    return { refetchInterval, isPolling: isEnabled && isPolling };
}
